import { Container } from '@hivebrite/design-system/Container';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import {
  BLOCKS,
  BlockWrapper,
} from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';
import useDisplayNavigationDropShadow from 'hb-react/shared/hooks/theme/useDisplayNavigationDropShadow';
import useSecondaryNavigationTheme from 'hb-react/shared/hooks/theme/useSecondaryNavigationTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import TemplateContainer from '../common/TemplateContainer';
import { DEFAULT_ZONE } from '../utils';

export const ZONE_IDS = {
  appHeader: 'appHeader',
  headerControls: 'headerControls',
  filters: 'filters',
  slider: 'slider',
  tags: 'tags',
  empty: 'empty',
  side: 'side',
};

const DEFAULT_ZONES = {
  appHeader: [BLOCKS.appHeader.id],
  headerControls: [BLOCKS.headerControls.id],
  filters: [BLOCKS.filters.id],
  slider: [BLOCKS.slider.id],
  tags: [BLOCKS.tags.id],
  empty: [BLOCKS.emptyContent.id],
  side: [BLOCKS.side.id],
};

const OneColumnTemplate = ({
  colOffset = 0,
  colWidth = 1,
  hasFilters,
  topicId,
  variant,
  zones,
}) => {
  const displayNavigationDropShadow = useDisplayNavigationDropShadow();
  const secondaryNavigationTheme = useSecondaryNavigationTheme();
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'xl' || breakpoint === 'l';

  return (
    <TemplateContainer id="one-column-template">
      {!topicId && zones[ZONE_IDS.appHeader] && (
        <BlockWrapper
          withShadow={
            displayNavigationDropShadow && variant !== 'max' && !hasFilters
          }
        >
          {zones[ZONE_IDS.appHeader]}
        </BlockWrapper>
      )}
      {zones[ZONE_IDS.headerControls] && (
        <Container>
          <Grid width="100%">
            <Row>
              <Col width={1}>
                <BlockWrapper mt={8} mb={-3}>
                  {zones[ZONE_IDS.headerControls]}
                </BlockWrapper>
              </Col>
            </Row>
          </Grid>
        </Container>
      )}
      {zones[ZONE_IDS.filters] && (
        <ThemeProvider
          theme={secondaryNavigationTheme.themeName}
          fontBrightness={secondaryNavigationTheme.fontBrightness}
        >
          <BlockWrapper
            py={6}
            bg={secondaryNavigationTheme.backgroundColor}
            withShadow={displayNavigationDropShadow}
          >
            <Container height="auto" display="block">
              <Grid rowGap={0}>
                <Row>
                  <Col offset={colOffset} width={colWidth}>
                    {zones[ZONE_IDS.filters]}
                  </Col>
                </Row>
              </Grid>
            </Container>
          </BlockWrapper>
        </ThemeProvider>
      )}
      <Container height="auto" display="block">
        <Grid rowGap={0}>
          {zones[ZONE_IDS.slider] ? (
            <Row>
              <Col offset={colOffset} width={colWidth}>
                <BlockWrapper mt={6} $emptyStyle={{ marginTop: 0 }}>
                  {zones[ZONE_IDS.slider]}
                </BlockWrapper>
              </Col>
            </Row>
          ) : null}
          {zones[ZONE_IDS.tags] ? (
            <Row>
              <Col offset={colOffset} width={colWidth}>
                <BlockWrapper mt={13} $emptyStyle={{ marginTop: 0 }}>
                  {zones[ZONE_IDS.tags]}
                </BlockWrapper>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col
              offset={colOffset}
              width={
                zones[ZONE_IDS.side]
                  ? {
                      _: 1,
                      l: 8 / 12,
                      xl: 9 / 12,
                    }
                  : colWidth
              }
            >
              {zones[DEFAULT_ZONE]?.map((block, index) => (
                <BlockWrapper key={index} mt={6} $emptyStyle={{ marginTop: 0 }}>
                  {block}
                </BlockWrapper>
              ))}
              {zones[ZONE_IDS.empty] && (
                <BlockWrapper mt={6} mb={10}>
                  {zones[ZONE_IDS.empty]}
                </BlockWrapper>
              )}
            </Col>
            {zones[ZONE_IDS.side] && isDesktop ? (
              <Col
                offset={colOffset}
                width={{
                  _: 0,
                  l: 4 / 12,
                  xl: 3 / 12,
                }}
              >
                <BlockWrapper mt={6}>{zones[ZONE_IDS.side]}</BlockWrapper>
              </Col>
            ) : null}
          </Row>
        </Grid>
      </Container>
    </TemplateContainer>
  );
};

export default {
  id: 'one_column',
  zones: DEFAULT_ZONES,
  component: OneColumnTemplate,
};
